const STORAGE_KEY = 'shopify_wishlist';
const LEGACY_STORAGE_KEYS = ['cityfashion_wishlist'];

class ThemeWishlist {
  constructor() {
    this.storageKey = window.themeWishlistCustomerId ? `${STORAGE_KEY}_${window.themeWishlistCustomerId}` : STORAGE_KEY;
    this.items = this.load();
    this.bindEvents();
    this.sync();
  }

  load() {
    const saved = this.safeParse(localStorage.getItem(this.storageKey));
    if (Array.isArray(saved)) return this.normalize(saved);

    for (const key of [STORAGE_KEY, ...LEGACY_STORAGE_KEYS]) {
      const legacy = this.safeParse(localStorage.getItem(key));
      if (Array.isArray(legacy)) return this.normalize(legacy);
    }

    return [];
  }

  safeParse(value) {
    if (!value) return null;

    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  normalize(items) {
    return items
      .map((item) => {
        if (typeof item === 'string') {
          return { handle: item, id: item };
        }

        return item;
      })
      .filter((item) => item?.handle || item?.id)
      .filter((item, index, list) => {
        const key = this.itemKey(item);
        return list.findIndex((candidate) => this.itemKey(candidate) === key) === index;
      });
  }

  itemKey(item) {
    return String(item.handle || item.id);
  }

  save() {
    localStorage.setItem(this.storageKey, JSON.stringify(this.items));
    window.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { items: this.items } }));
  }

  has(key) {
    return this.items.some((item) => this.itemKey(item) === String(key));
  }

  toggle(product) {
    const key = this.itemKey(product);
    const existingIndex = this.items.findIndex((item) => this.itemKey(item) === key);

    if (existingIndex >= 0) {
      this.items.splice(existingIndex, 1);
    } else {
      this.items.unshift(product);
    }

    this.save();
    this.sync();
  }

  remove(key) {
    this.items = this.items.filter((item) => this.itemKey(item) !== String(key));
    this.save();
    this.sync();
  }

  buttonProduct(button) {
    return {
      id: button.dataset.productId,
      handle: button.dataset.productHandle,
      title: button.dataset.productTitle,
      url: button.dataset.productUrl,
      image: button.dataset.productImage,
      price: button.dataset.productPrice,
      comparePrice: button.dataset.productComparePrice,
    };
  }

  bindEvents() {
    document.addEventListener('click', (event) => {
      const button = event.target.closest('[data-wishlist-button]');
      if (button) {
        event.preventDefault();
        this.toggle(this.buttonProduct(button));
        return;
      }

      const removeButton = event.target.closest('[data-wishlist-remove]');
      if (removeButton) {
        event.preventDefault();
        this.remove(removeButton.dataset.wishlistRemove);
      }
    });

    window.addEventListener('storage', (event) => {
      if (event.key !== this.storageKey) return;
      this.items = this.load();
      this.sync();
    });

    document.addEventListener('shopify:section:load', () => this.sync());
  }

  sync() {
    this.syncButtons();
    this.syncCount();
    this.renderPage();
  }

  syncButtons() {
    document.querySelectorAll('[data-wishlist-button]').forEach((button) => {
      const product = this.buttonProduct(button);
      const saved = this.has(this.itemKey(product));
      const title = saved ? `Remove ${product.title} from wishlist` : `Add ${product.title} to wishlist`;

      button.classList.toggle('is-active', saved);
      button.setAttribute('aria-pressed', saved ? 'true' : 'false');
      button.setAttribute('aria-label', title);
    });
  }

  syncCount() {
    const count = String(this.items.length);

    document.querySelectorAll('[data-wishlist-count]').forEach((node) => {
      node.textContent = count;
      node.dataset.count = count;
    });
  }

  renderPage() {
    const page = document.querySelector('[data-wishlist-page]');
    const grid = page?.querySelector('[data-wishlist-grid]');
    if (!page || !grid) return;

    page.dataset.empty = this.items.length === 0 ? 'true' : 'false';
    grid.innerHTML = this.items.map((item) => this.cardMarkup(item)).join('');
  }

  cardMarkup(item) {
    const key = this.itemKey(item);
    const title = this.escape(item.title || 'Saved product');
    const url = this.escape(item.url || `/products/${item.handle}`);
    const image = this.escape(item.image || '');
    const price = this.escape(item.price || '');

    return `
      <li class="wishlist-card" data-wishlist-item="${this.escape(key)}">
        <a class="wishlist-card__image" href="${url}" aria-label="${title}">
          ${image ? `<img src="${image}" alt="${title}" loading="lazy">` : ''}
        </a>
        <div class="wishlist-card__info">
          <p class="wishlist-card__title"><a href="${url}">${title}</a></p>
          ${price ? `<p class="wishlist-card__price">${price}</p>` : ''}
        </div>
        <button class="wishlist-card__remove" type="button" data-wishlist-remove="${this.escape(key)}">Remove</button>
      </li>
    `;
  }

  escape(value) {
    return String(value ?? '').replace(/[&<>"']/g, (char) => {
      const entities = {
        '&': '&amp;',
        '<': '&lt;',
        '>': '&gt;',
        '"': '&quot;',
        "'": '&#039;',
      };

      return entities[char];
    });
  }
}

if (!window.themeWishlist) {
  window.themeWishlist = new ThemeWishlist();
}
